import { defineStore } from "pinia";
import { ref } from "vue";
import {
    getCountryCodeFromTimezone,
    getCountryCodeFromIPAddress,
} from "@/helpers/timezone";
import { User } from "@/helpers/interfaces/auth";
import { generateUUID } from "@/helpers/uuid";

/**
 * Session store to manage user session data such as authentication status, origin,
 * country code, language, session ID, email address, and user details.
 * The store persists data in sessionStorage to maintain state across page
 * reloads within the same session.
 */
export const useSessionStore = defineStore(
    "session",
    () => {
        // ── State ──────────────────────────────────────────────────────────────
        const isAuthenticated = ref(false);
        const origin = ref<string | null>(null);
        const countryCode = ref<string | null>(null);
        const detectedCountryCode = ref<string | null>(null);
        const language = ref<string | null>(null);
        const sessionId = ref<string | null>(null);
        const emailAddress = ref<string | null>(null);
        const user = ref<User | null>(null);

        // ── Actions ────────────────────────────────────────────────────────────
        function setIsAuthenticated(value: boolean) {
            isAuthenticated.value = value;
        }

        function setOrigin(value: string | null) {
            origin.value = value;
        }

        async function setDefaultCountryCode() {
            const { countryNamesAndIsoCodes } =
                await import("@esenda/core-ui/constants");

            /**
             * Sets the default country code based on the user's timezone
             * If no timezone is found, it falls back on IP address lookup.
             */
            if (countryCode.value === null || countryCode.value.length === 0) {
                countryCode.value = getCountryCodeFromTimezone();
            }

            if (countryCode.value === undefined) {
                countryCode.value = await getCountryCodeFromIPAddress();
            } else {
                if (
                    countryCode.value === null ||
                    countryCode.value.length !== 2
                ) {
                    countryCode.value = await getCountryCodeFromIPAddress();
                }
            }

            if (detectedCountryCode.value === null) {
                detectedCountryCode.value = countryCode.value;
            }

            /**
             * If the country code exists in our supported countries then assign it,
             * otherwise use the default.
             * Validate the persisted/URL-provided countryCode independently so that
             * an unsupported value always falls back to "GB".
             */
            const isCodeSupported = (_code: string | null) => {
                return (
                    !!_code &&
                    countryNamesAndIsoCodes.some(({ code }) => code === _code)
                );
            };

            if (!isCodeSupported(countryCode.value)) {
                countryCode.value = "GB";
            }

            if (!isCodeSupported(detectedCountryCode.value)) {
                detectedCountryCode.value = countryCode.value || "GB";
            }
        }

        function updateCountryCode(manuallySelectedCountryCode: string) {
            /**
             * Updates the country code based on a user selected value
             */
            countryCode.value = manuallySelectedCountryCode;
        }

        function setDefaultLanguage() {
            /**
             * Updates the default language only if the stored value is null, preventing resets
             * on page refresh or navigation.
             * Sets the default language based on a the language configured in the browser
             * It gets the first value from the navigator.languages list if this list is not
             * undefined. Otherwise, it gets the value from navigator.language.
             * If both navigator.languages and navigator.language are undefined, it defaults
             * to en-GB
             */
            if (!language.value) {
                language.value =
                    navigator.languages && navigator.languages.length
                        ? navigator.languages[0]
                        : navigator.language || "en-GB";
            }
        }

        function updateLanguage(manuallySelectedLanguage: string) {
            /**
             * Updates the language code based on a user selected value
             */
            language.value = manuallySelectedLanguage;
        }

        function setSessionId(id = generateUUID()) {
            /**
             * Sets a user session id to an UUIDv4 value
             */
            sessionId.value = id;
        }

        function setEmailAddress(email: string | null) {
            emailAddress.value = email;
        }

        function setUser(value: any) {
            /**
             * Sets authenticated user details
             */
            user.value = value;
        }

        return {
            isAuthenticated,
            origin,
            countryCode,
            detectedCountryCode,
            language,
            sessionId,
            emailAddress,
            user,
            setIsAuthenticated,
            setOrigin,
            setDefaultCountryCode,
            updateCountryCode,
            setDefaultLanguage,
            updateLanguage,
            setSessionId,
            setEmailAddress,
            setUser,
        };
    },
    {
        persist: {
            pick: [
                "isAuthenticated",
                "origin",
                "countryCode",
                "detectedCountryCode",
                "language",
                "sessionId",
                "emailAddress",
                "user",
            ],
            storage: sessionStorage,
        },
    },
);
